/**
 * What the admin pages load, and a small cache shared between them so moving
 * from page to page shows the last figures at once while fresh ones load.
 */
import { useEffect, useSyncExternalStore } from "react";
import { api, errorMessage, type SyncState, type User } from "../lib/api";

// ---------------------------------------------------------------- cache

interface Entry {
  data?: unknown;
  error?: string;
  /** When data or error last arrived. */
  at: number;
  pending?: Promise<void>;
}

const store = new Map<string, Entry>();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((fn) => fn());
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Fetches url into the cache. Callers asking at the same time share one request. */
export function refresh(url: string): Promise<void> {
  const old = store.get(url);
  if (old?.pending) return old.pending;
  const pending = api.get<unknown>(url).then(
    (data) => {
      store.set(url, { data, at: Date.now() });
      emit();
    },
    (e) => {
      store.set(url, { data: store.get(url)?.data, error: errorMessage(e), at: Date.now() });
      emit();
    },
  );
  store.set(url, { ...(old ?? { at: 0 }), pending });
  emit();
  return pending;
}

/** Refetches everything cached under a prefix, e.g. after an action changed it. */
export function refreshAll(prefix: string) {
  return Promise.all([...store.keys()].filter((k) => k.startsWith(prefix)).map(refresh));
}

export interface Resource<T> {
  data: T | undefined;
  /** Set when the last request failed; data may still hold an older copy. */
  error: string | undefined;
  loading: boolean;
  reload: () => Promise<void>;
  /** Changes the cached copy straight away, e.g. after an action that returns the new row. */
  update: (fn: (d: T) => T) => void;
}

/** GETs url (or nothing, while it's null), reusing a copy younger than maxAge. */
export function useResource<T>(url: string | null, maxAge = 20_000): Resource<T> {
  const entry = useSyncExternalStore(subscribe, () => (url ? store.get(url) : undefined));
  useEffect(() => {
    if (!url) return;
    const e = store.get(url);
    if (!e || (!e.pending && Date.now() - e.at > maxAge)) refresh(url);
  }, [url, maxAge]);
  return {
    data: entry?.data as T | undefined,
    error: entry?.error,
    loading: !!entry?.pending,
    reload: () => (url ? refresh(url) : Promise.resolve()),
    update: (fn) => {
      const e = url ? store.get(url) : undefined;
      if (!url || !e || e.data === undefined) return;
      store.set(url, { ...e, data: fn(e.data as T) });
      emit();
    },
  };
}

// ---------------------------------------------------------------- endpoints

export const URLS = {
  users: "/api/admin/users",
  invites: "/api/admin/invites",
  storage: "/api/admin/storage",
  sync: "/api/admin/sync",
  hosting: "/api/admin/hosting",
  volume: "/api/admin/storage/volume",
  facets: "/api/admin/audit/facets",
};

export interface AdminUser extends User {
  lastSeen: number | null;
  invitedBy: number | null;
  /** People a trusted user has invited since their limit was last reset. */
  invitesCounted: number | null;
  /** Projects they own, outside the recycle bin. */
  projects: number;
  /** Live connections right now, one per open project tab. */
  sockets: number;
}

/** A project someone owns, listed before their account is deleted along with it. */
export interface OwnedProject {
  id: string;
  name: string;
  /** Everyone else on it, by display name. */
  sharedWith: string[];
}

export interface UsersResponse {
  users: AdminUser[];
  inviteLimit: number;
  /** How many projects a non-admin may own; 0 means no limit. */
  projectLimit: number;
}

export interface Invite {
  id: string;
  note: string;
  createdBy: number | null;
  createdAt: number;
  expiresAt: number;
  /** When it was used up or revoked. */
  usedAt: number | null;
  resetFor: string | null;
  grants: Array<{ projectId: string; projectName: string; role: string }>;
  maxUses: number;
  useCount: number;
}

export interface ProjectUsage {
  id: string;
  name: string;
  owner: string;
  ownerName: string;
  members: number;
  updatedAt: number;
  deletedAt: number | null;
  files: number;
  history: number;
  builds: number;
  cache: number;
  total: number;
}

export interface StorageReport {
  scannedAt: number;
  disk: { total: number; used: number; free: number };
  database: number;
  other: number;
  projects: ProjectUsage[];
}

export interface SyncRow {
  id: string;
  name: string;
  owner: { username: string; displayName: string } | null;
  updatedAt: number;
  repo: { name: string; branch: string; url: string } | null;
  /** What switching it on reconnects to, or roughly what it creates. */
  next: string | null;
  autoSync: boolean;
  syncState: SyncState & { formerRepo?: string };
}

export interface SyncResponse {
  account: string | null;
  projects: SyncRow[];
}

export const syncProblem = (r: SyncRow) => !!r.repo && (r.syncState.status === "error" || r.syncState.status === "conflict");

export interface AuditEntry {
  id: number;
  at: number;
  action: string;
  ok: boolean;
  status: number;
  error: string;
  username: string;
  projectId: string | null;
  projectName: string;
  targetUsername: string;
  ip: string;
  userAgent: string;
  details: Record<string, unknown>;
}

export interface AuditPage {
  entries: AuditEntry[];
  next: number | null;
}

export interface AuditFacets {
  actions: Array<{ action: string; label: string }>;
  users: string[];
  projects: Array<{ id: string; name: string }>;
  retentionDays: number;
}

// ---------------------------------------------------------------- Fly

export type CpuKind = "shared" | "performance";

export interface SizeOption {
  name: string;
  cpuKind: CpuKind;
  cpus: number;
  memory: Array<{ mb: number; perSecond: number }>;
}

export interface Machine {
  id: string;
  name: string;
  state: string;
  region: string;
  size: string | null;
  cpuKind: CpuKind;
  cpus: number;
  memoryMb: number;
  running: boolean;
  perSecond: number;
  isThis: boolean;
}

export interface Day {
  start: number;
  running: number;
  stopped: number;
  storage: number;
  estimated: number;
  /** What the day so far would have cost had the server never stopped. */
  allDay: number;
  runningSeconds: number;
}

export interface Month {
  start: number;
  end: number;
  billedSince: number;
  trackedSince: number;
  duty: number | null;
  dutySpanSeconds: number;
  runningSeconds: number;
  accrued: { running: number; stopped: number; storage: number; estimated: number; total: number };
  remainingSeconds: number;
  expected: number;
  days: Day[];
}

/** What /api/admin/hosting always sends; off Fly, that's all. */
export interface HostingBase {
  at: number;
  startedAt: number;
  app: string | null;
  machineId: string | null;
  region: string;
  apiEnabled: boolean;
}

export interface Hosting extends HostingBase {
  flyError: string;
  markup: number;
  regionPriced: boolean;
  rates: { volumeGbMonth: number; rootfsGbMonth: number; snapshotGbMonth: number; snapshotFreeGb: number; monthSeconds: number };
  sizes: SizeOption[];
  minMemoryMb: number;
  machines: Machine[];
  volume: { gb: number; count: number; source: "fly" | "disk" };
  snapshots: { count: number; bytes: number } | null;
  rootfsBytes: number | null;
  live: { machines: number; stopped: number; volume: number; snapshots: number; perSecond: number };
  month: Month;
  maxConcurrentCompiles: number;
}

export type HostingResponse = HostingBase & Partial<Hosting>;

/** The full report, or null when the server isn't on Fly. */
export const fullHosting = (h: HostingResponse | undefined) => (h?.month ? (h as Hosting) : null);

export interface FlyVolume {
  id: string;
  name: string;
  state: string;
  region: string;
  sizeGb: number;
  encrypted: boolean;
  createdAt: number | null;
  snapshotRetention: number | null;
  autoBackup: boolean | null;
}

export interface VolumeStatus {
  app: string | null;
  machineId: string | null;
  region: string | null;
  apiEnabled: boolean;
  volume?: FlyVolume;
  autoExtend?: { thresholdPercent: number; addSizeGb: number | null; limitGb: number | null } | null;
  snapshots?: Array<{ id: string; size: number; createdAt: number; retentionDays: number | null; status: string | null }> | null;
}

// ---------------------------------------------------------------- formatting

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

export function percent(part: number, whole: number): string {
  if (!whole || !part) return "0%";
  const p = (part / whole) * 100;
  if (p < 0.1) return "<0.1%";
  return `${p < 10 ? p.toFixed(1) : Math.round(p)}%`;
}

const usd = new Intl.NumberFormat("en-AU", { style: "currency", currency: "USD", currencyDisplay: "narrowSymbol" });
export const money = (n: number) => usd.format(n);

/** "1 person", "3 people". */
export const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** How full the disk is, as the chips and meters colour it. */
export const fullnessTone = (f: number) => (f >= 0.9 ? "bad" : f >= 0.8 ? "warn" : "ok");

export const monthName = (t: number) => new Date(t).toLocaleDateString("en-AU", { month: "long", timeZone: "UTC" });
